import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

const Navbar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  // Detectar scroll para cambiar el estilo del navbar con throttling
  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20)
          ticking = false
        })
        ticking = true
      }
    }

    // Set initial state
    setIsScrolled(window.scrollY > 20)

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToSection = (sectionId) => {
    // Si no estamos en la página principal, navegar a ella primero
    if (location.pathname !== "/") {
      navigate("/")
      // Debemos esperar a que la navegación termine antes de hacer scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId)
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          })
        }
      }, 100)
    } else {
      const element = document.getElementById(sectionId)
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        })
      }
    }
    setIsMenuOpen(false)
  }

  const navigateTo = (path) => {
    navigate(path)
    setIsMenuOpen(false)
  }

  const navItems = [
    { label: "Inicio", id: "hero", action: () => scrollToSection("hero") },
    { label: "Nosotros", id: "about", action: () => scrollToSection("about") },
    { label: "Doctores", id: "doctors", action: () => scrollToSection("doctors") },
    { label: "Servicios", id: "services", action: () => scrollToSection("services") },
    { label: "Contacto", id: "contact", action: () => scrollToSection("contact") }
  ]

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled || isMenuOpen
          ? "bg-white shadow-lg py-3"
          : "bg-transparent py-4"
      }`}
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <button 
            onClick={() => scrollToSection("hero")}
            className="flex items-center group focus:outline-none"
          >
            <div className="relative overflow-hidden rounded-full shadow-lg mr-3 transition-transform duration-300 group-hover:scale-105">
              <img 
                src="/logo_complete.png" 
                alt="Logo Cardiología del Norte"
                className="h-10 w-10 md:h-14 md:w-14 object-cover"
              />
            </div>
            <div className="flex flex-col items-start">
              <h1 className={`text-xl md:text-2xl font-bold font-serif leading-tight tracking-tight text-left transition-colors duration-500 ${
                isScrolled || isMenuOpen ? "text-primary" : "text-white"
              }`}>
                Cardiología del Norte
              </h1>

            </div>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={item.action}
                className={`relative px-4 py-2 font-medium transition-colors group bg-transparent border-none cursor-pointer ${
                  isScrolled
                    ? "text-gray-700 hover:text-primary"
                    : "text-white hover:text-cyan-400"
                }`}
              >
                {item.label}
                <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary transform scale-x-0 origin-left transition-transform duration-300 group-hover:scale-x-100 ease-out"></span>
              </button>
            ))}
            
            <div className={`ml-6 pl-6 border-l ${isScrolled ? "border-gray-300" : "border-white/30"}`}>
              <button
                onClick={() => scrollToSection("contact")}
                className={`inline-flex items-center justify-center px-6 py-2.5 text-sm font-bold transition-all duration-300 rounded-full hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 hover:scale-105 active:scale-95 ${
                  isScrolled
                    ? "bg-gradient-to-r from-primary to-blue-700 text-white shadow-primary/30"
                    : "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-cyan-500/30"
                }`}
              >
                Agendar Cita
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-full transition-colors focus:outline-none ${
                isScrolled || isMenuOpen
                  ? "text-gray-700 hover:bg-gray-100"
                  : "text-white hover:bg-white/10"
              }`}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <XMarkIcon className="w-7 h-7" /> : <Bars3Icon className="w-7 h-7" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out absolute top-full left-0 right-0 ${
          isMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-white/95 backdrop-blur-md shadow-xl border-t border-gray-100">
          <div className="container mx-auto px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={item.action}
                className="block w-full text-left px-4 py-3 text-gray-800 font-medium hover:text-primary hover:bg-cyan-50/50 rounded-lg transition-all border-b border-gray-50 last:border-0"
              >
                {item.label}
              </button>
            ))}

            {/* Mobile CTA Button */}
            <div className="pt-4 mt-2">
              <button
                onClick={() => scrollToSection("contact")}
                className="block w-full text-center px-4 py-3.5 font-bold text-white bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-md active:scale-95"
              >
                Agendar Cita
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
